export interface ReservationCreate {
    eventName: string;
    reservationEventType: ReservationEventType;
    requiredAgents: number;
    startDateTime: string;
    endDateTime: string;
    address: Address;
    description?: string;
}

export interface ReservationUpdateDTO {
    eventName: string;
    reservationEventType: ReservationEventType;
    requiredAgents: number;
    startDateTime: string;
    endDateTime: string;
    address: Address;
    description?: string;
}

export interface ReservationListBase {
    id: number;
    eventName: string;
    reservationEventType: ReservationEventType;
    requiredAgents: number;
    startDateTime: string;
    endDateTime: string;
    address: Address;
    reservationClientStatus: ReservationClientStatus;
    reservationAdminStatus: ReservationAdminStatus;
    clientName: string;
    clientId: number;
    serviceFeedbackId: number;
    createdAt: string;
}

export interface ReservationList extends ReservationListBase{
    offers: Offer[];
}

export interface CustomerReservationList extends ReservationListBase{
    offers: CustomerOffer[];serviceFeedbackId: number;
}

export interface ReservationDetail {
    id: number;
    eventName: string;
    reservationEventType: ReservationEventType;
    requiredAgents: number;
    startDateTime: string;
    endDateTime: string;
    address: Address;
    description?: string;
    reservationClientStatus: ReservationClientStatus;
    reservationAdminStatus: ReservationAdminStatus;
    client: Customer;
    serviceFeedbackId: number;
    createdAt: string;
    updatedAt: string;
    offers: OfferDetail[];
}

export interface OfferCreate {
    price: number;
    description: string;
    agentIds: number[];
}

export interface Offer {
    id: number;
    price: number;
    description: string;
    status: OfferStatus;
    createdAt: string;
    agents: Agent[];
}

export interface CustomerOffer {
    id: number;
    price: number;
    description: string;
    status: OfferStatus;
    createdAt: string;
}


export interface OfferDetail extends Offer {
}

export interface Customer {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    profileImage: string;
}

export interface Agent {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    profileImage: string;
    birthDate?: string;
    age?: number;
    weight?: number;
    height?: number;
    isAvailable?: boolean;
    specialization?: string;
    rating?: number;
    yearsOfExperience?: number;
    bio?: string;
}

export interface Address {
    street?: string;
    streetNumber?: string;
    postalCode?: string;
    city?: string;
}

export interface ReservationSearchCriteria {
    search?: string;
    clientId?: number;
    startDateFrom?: string;
    startDateTo?: string;
    clientStatus?: string;
    adminStatus?: string;
}

export interface OfferWithActions extends Offer {
    canEdit: boolean;
    canDelete: boolean;
    canAddAgents: boolean;
    assignedAgentNames: string;
    assignedAgentCount: number;
    formattedPrice?: string;
    formattedDate?: string;
}

export interface OfferActionResponse {
    success: boolean;
    message: string;
    data: ReservationDetail;
    timestamp: string;
}

export enum ReservationEventType {
    SECURITY_GUARDING = 'SECURITY_GUARDING',
    EVENT_SECURITY = 'EVENT_SECURITY',
    PERSONAL_PROTECTION = 'PERSONAL_PROTECTION',
    SURVEILLANCE = 'SURVEILLANCE',
    SECURITY_AUDIT = 'SECURITY_AUDIT',
    TRANSPORT_SECURITY = 'TRANSPORT_SECURITY',
    OTHER = 'OTHER'
}

export enum ReservationClientStatus {
    PENDING = 'PENDING',
    ACCEPTED = 'ACCEPTED',
    REJECTED_BY_CLIENT = 'REJECTED_BY_CLIENT',
    REQUESTED_NEW_OFFER = 'REQUESTED_NEW_OFFER'
}

export enum ReservationAdminStatus {
    WAITING_OFFER = 'WAITING_OFFER',
    OFFER_CREATED = 'OFFER_CREATED',
    WAITING_CONFIRMATION = 'WAITING_CONFIRMATION',
    CONFIRMED = 'CONFIRMED',
    REJECTED_BY_ADMIN = 'REJECTED_BY_ADMIN',
    CANCELLED = 'CANCELLED'
}

export enum OfferStatus {
    PENDING = 'PENDING',
    ACCEPTED = 'ACCEPTED',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED'
}

export const ReservationEventTypeLabels = {
    [ReservationEventType.SECURITY_GUARDING]: 'Security Guarding',
    [ReservationEventType.EVENT_SECURITY]: 'Event Security',
    [ReservationEventType.PERSONAL_PROTECTION]: 'Personal Protection',
    [ReservationEventType.SURVEILLANCE]: 'Surveillance',
    [ReservationEventType.SECURITY_AUDIT]: 'Security Audit',
    [ReservationEventType.TRANSPORT_SECURITY]: 'Transport Security',
    [ReservationEventType.OTHER]: 'Other'
};

export const ReservationClientStatusLabels = {
    [ReservationClientStatus.PENDING]: 'Pending',
    [ReservationClientStatus.ACCEPTED]: 'Accepted',
    [ReservationClientStatus.REJECTED_BY_CLIENT]: 'Rejected by Client',
    [ReservationClientStatus.REQUESTED_NEW_OFFER]: 'Nouvelle offre demandée'
};

export const ReservationAdminStatusLabels = {
    [ReservationAdminStatus.WAITING_OFFER]: 'Waiting for Offer',
    [ReservationAdminStatus.OFFER_CREATED]: 'Offer Created',
    [ReservationAdminStatus.CONFIRMED]: 'Confirmed',
    [ReservationAdminStatus.REJECTED_BY_ADMIN]: 'Rejected by Admin',
    [ReservationAdminStatus.CANCELLED]: 'Cancelled'
};

export const OfferStatusLabels = {
    [OfferStatus.PENDING]: 'Pending',
    [OfferStatus.ACCEPTED]: 'Accepted',
    [OfferStatus.REJECTED]: 'Rejected',
    [OfferStatus.CANCELLED]: 'Cancelled'
};

export const OfferStatusColors = {
    [OfferStatus.PENDING]: '#ffc107',
    [OfferStatus.ACCEPTED]: '#28a745',
    [OfferStatus.REJECTED]: '#dc3545',
    [OfferStatus.CANCELLED]: '#6c757d'
};

export const OfferStatusIcons = {
    [OfferStatus.PENDING]: 'clock',
    [OfferStatus.ACCEPTED]: 'check-circle',
    [OfferStatus.REJECTED]: 'x-circle',
    [OfferStatus.CANCELLED]: 'ban'
};

export type ReservationAction = 'accept' | 'reject' | 'request_new' | 'update' | 'cancel' | 'delete' | 'give_feedback' | 'see_feedback';

export interface ReservationActionState {
    canAcceptOffer: boolean;
    canRejectOffer: boolean;
    canRequestNewOffer: boolean;
    canUpdateReservation: boolean;
    canCancelReservation: boolean;
    canDeleteReservation: boolean;
    canGiveFeedback?: boolean;
    canSeeFeedbackDetails?: boolean;
    showSingleOfferPrice: boolean;
    singleOfferPrice?: string;
}